/** @jsx jsx */
import { jsx } from '@emotion/react';

import * as React from 'react';

import { useParams } from 'js/lib/useRouter';
import user from 'js/lib/user';

import { ContextualHelp, InlineNotification, TextField, Typography2 } from '@coursera/cds-core';
import { useTracker } from '@coursera/event-pulse/react';

import { usePendingAdminCoachMessageId } from 'bundles/enterprise-admin-hooks/hooks/usePendingAdminCoachMessageId';
import ModalFooter from 'bundles/enterprise-admin-users/components/ModalFooter';

import _t from 'i18n!nls/enterprise-ui';

export const NAME_CONFIRM_THRESHOLD = 100;

type Props = {
  count: number;
  overThresholdMessage: React.ReactNode | string;
  underThresholdMessage: React.ReactNode | string;
  error?: string;
  processing: boolean;
  underThresholdTitle?: string;
  confirmLabel?: string;
  // This prop is added to bypass having to convert ImportLearnersModal to a functional component in order to use useTracker
  trackingName?: 'enterprise_bulk_send_learner_invites';
  onPreviousStep?: () => void;
  handleClose: () => void;
  onSubmit: () => void;
};

const BulkEmailConfirm = ({
  count,
  onPreviousStep,
  handleClose,
  error,
  processing,
  overThresholdMessage,
  underThresholdMessage,
  underThresholdTitle = _t('Send Invitations?'),
  confirmLabel = _t('Send invitations'),
  trackingName,
  onSubmit,
}: Props) => {
  const track = useTracker();
  // Present only when launched from the Admin Coach with a pending coach action, so the invite event
  // can be tied back to the originating chat message.
  const coachMessageId = usePendingAdminCoachMessageId();
  const { thirdPartySlug, programSlug } = useParams();
  const [name, setName] = React.useState<string>('');
  const [nameError, setError] = React.useState<string | undefined>(undefined);
  const adminName = user.get().display_name ?? '';
  const isValid = name.trim() === adminName.trim();
  let validationStatus: 'success' | 'error' | undefined;
  if (isValid) {
    validationStatus = 'success';
  }
  if (nameError) {
    validationStatus = 'error';
  }

  const handleSubmit = () => {
    if (trackingName) {
      track('click_button', {
        button: { name: trackingName },
        enterprise: {
          ...(thirdPartySlug ? { organizationSlug: thirdPartySlug } : {}),
          ...(programSlug ? { programSlug } : {}),
        },
        ...(coachMessageId ? { coach: { chatMessageId: coachMessageId } } : {}),
      });
    }
    if (count <= NAME_CONFIRM_THRESHOLD || isValid) {
      return onSubmit();
    }
    if (!isValid) {
      setError(_t('Name does not match'));
    }
  };

  const nameField = (
    <TextField
      css={{ marginTop: 'var(--cds-spacing-300)' }}
      label={_t('Type your name below to confirm this action')}
      placeholder={_t('Type your name here')}
      value={name}
      onChange={(event) => {
        setName(event.target.value);
        setError(undefined);
      }}
      validationStatus={validationStatus}
      validationLabel={nameError}
      supportText={_t('Tip: The name in your account is #{email}', { email: user.get().display_name })}
      renderLabel={(labelComponent: React.ReactElement) => (
        <ContextualHelp
          id="contextual-help-label"
          helpToggleProps={{
            'aria-label': _t('Help'),
          }}
          label={labelComponent}
        >
          <Typography2 component="p">
            {_t(
              'In order to prevent accidental emailing of bulk messages we ask that you carefully consider the number of learners that will receive this message and type your name to confirm your awareness.'
            )}
          </Typography2>
        </ContextualHelp>
      )}
      inputProps={{
        'aria-labelledby': 'contextual-help-label',
        id: 'BulkEmailConfirmNameInput-input',
      }}
    />
  );
  const overThreshold = count > NAME_CONFIRM_THRESHOLD;
  return (
    <div className="rc-BulkEmailConfirm">
      <Typography2 variant="titleMedium" component="h2">
        {overThreshold ? _t('Confirmation Required') : underThresholdTitle}
      </Typography2>
      <Typography2 component="p" css={{ marginTop: 'var(--cds-spacing-300)' }}>
        {overThreshold ? overThresholdMessage : underThresholdMessage}
      </Typography2>
      <Typography2 component="p">{_t('This action cannot be undone.')}</Typography2>
      {overThreshold && nameField}
      {error && (
        <InlineNotification severity="error" css={{ marginTop: 'var(--cds-spacing-300)' }}>
          <Typography2 component="p">{error}</Typography2>
        </InlineNotification>
      )}
      <ModalFooter
        onContinue={handleSubmit}
        onContinueLabel={confirmLabel}
        onCancel={handleClose}
        onPrevious={onPreviousStep}
        disabled={processing}
      />
    </div>
  );
};

export default BulkEmailConfirm;
